import { Immutable } from "immer";
import React, { useEffect, useState } from "react";

import {
    setOptionValue,
    setSelectedCategory,
    setSelectedRootProduct,
} from "../../apps/configurator/reducers";
import {
    categoriesSelector,
    optionPanelsSelector,
    optionValueSelector,
    rootProductSelector,
    selectedCategoriesSelector,
} from "../../apps/configurator/selectors";
import {
    IOptionCode,
    IOptionSingleValue,
    IProduct,
} from "../../models/catalogue";
import { IProductID } from "../../models/nominals";
import { ITabCardsBlock } from "../../models/streamfield-blocks";
import { useViewport } from "../../store/common/hooks";
import { useAppDispatch, useAppSelector } from "../../store/reducers";
import { TabCardsBlock as TabCardsBlockComponent } from "./Presentation";

type IProps = Immutable<{
    tabCards: ITabCardsBlock;
    theme?: string;
    initialRootProductID?: IProductID | null;
}>;

export const TabCardsBlock = (props: IProps): React.ReactNode => {
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => setHasMounted(true), []);

    const dispatch = useAppDispatch();
    const rootProduct = useAppSelector((rootState) =>
        rootProductSelector(rootState.configurator),
    );
    const rootProducts = useAppSelector(
        (rootState) => rootState.configurator.entities.rootProducts,
    );
    const categories = useAppSelector((rootState) =>
        categoriesSelector(rootState.configurator),
    );
    const optionPanels = useAppSelector((rootState) =>
        optionPanelsSelector(rootState.configurator),
    );
    const selectedCategories = useAppSelector((rootState) =>
        selectedCategoriesSelector(rootState.configurator),
    );
    const selectedOptionValues = useAppSelector((rootState) =>
        optionValueSelector(rootState.configurator),
    );
    const viewport = useViewport();

    // Consumer pattern: before mount, use server-provided values (matches SSR).
    // After mount, use Redux (populated by ModularConfigurator).
    const effectiveRootProductID =
        hasMounted && rootProduct
            ? rootProduct.id
            : (props.initialRootProductID ?? null);
    const effectiveNamespace =
        hasMounted && rootProduct ? rootProduct.product_class_slug : null;
    const effectiveRootProducts = hasMounted ? rootProducts : null;
    const effectiveCategories = hasMounted ? categories : [];
    const effectiveOptionPanels = hasMounted ? optionPanels : [];
    const effectiveSelectedCategories = hasMounted ? selectedCategories : {};
    const effectiveSelectedOptionValues = hasMounted
        ? selectedOptionValues
        : {};

    const handleSelectRootProduct = (product: IProduct) => {
        dispatch(setSelectedRootProduct(product));
    };

    const handleSelectCategory = (
        payload: Parameters<typeof setSelectedCategory>[0],
    ) => {
        dispatch(setSelectedCategory(payload));
    };

    const handleSelectAttribute = (
        namespace: string,
        code: IOptionCode,
        index: number,
        totalNumValues: number,
        value: IOptionSingleValue,
    ) => {
        dispatch(
            setOptionValue({
                namespace,
                code,
                index,
                totalNumValues,
                value,
            }),
        );
    };

    return (
        <TabCardsBlockComponent
            tabCards={props.tabCards}
            pageRootProducts={effectiveRootProducts}
            pageRootProductID={effectiveRootProductID}
            pageProductNamespace={effectiveNamespace}
            pageCategorySelectors={effectiveCategories}
            pageOptionPanels={effectiveOptionPanels}
            pageSelectedCategories={effectiveSelectedCategories}
            pageSelectedOptionValues={effectiveSelectedOptionValues}
            viewport={viewport}
            theme={props.theme}
            onCardProductSelect={handleSelectRootProduct}
            onCardCategorySelect={handleSelectCategory}
            onCardAttributeSelect={handleSelectAttribute}
        />
    );
};
